import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ConfirmProvider } from "@/components/ui/confirm";
import { BetSlipProvider } from "@/context/BetSlipContext";
import { Shell } from "@/components/shell/Shell";
import { GeoGate } from "@/components/GeoGate";
import CookieConsent from "@/components/CookieConsent";
import NavigationScroll from "@/components/NavigationScroll";
import SportsSearch from "@/components/sportsbook/SportsSearch";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import LegalHub from "@/pages/legal/LegalHub";
import PolicyPage from "@/pages/legal/PolicyPage";
import PaymentResult from "@/pages/PaymentResult";
import Landing from "@/pages/Landing";
import Home from "@/pages/Home";
import Account from "@/pages/Account";
import Rewards from "@/pages/Rewards";
import CoinStore from "@/pages/CoinStore";
import Sportsbook from "@/pages/Sportsbook";
import SportsbookUPA from "@/pages/SportsbookUPA";
import OperatorUPA from "@/pages/OperatorUPA";
import Wallet from "@/pages/Wallet";
import MyBets from "@/pages/MyBets";
import Support from "@/pages/Support";
import Streamer from "@/pages/Streamer";
import Casino from "@/pages/Casino";
import Mines from "@/pages/Mines";
import Dice from "@/pages/Dice";
import Crash from "@/pages/Crash";
import CoinFlip from "@/pages/CoinFlip";
import Roulette from "@/pages/Roulette";
import Blackjack from "@/pages/Blackjack";
import Baccarat from "@/pages/Baccarat";
import Slots from "@/pages/Slots";
import HiLo from "@/pages/HiLo";
import Plinko from "@/pages/Plinko";
import Keno from "@/pages/Keno";
import DragonTower from "@/pages/DragonTower";
import { ORIGINAL_PAGE_ROUTES } from "@/casino/nativeGames";
import Admin from "@/pages/Admin";
import MarketOperations from "@/pages/MarketOperations";
import EventDetail from "@/pages/EventDetail";
import OutrightDetail from "@/pages/OutrightDetail";
import BlackjackOperator from "@/pages/BlackjackOperator";
import BlackjackDealerDisplay from "@/pages/BlackjackDealerDisplay";
import ProviderSandbox from "@/pages/ProviderSandbox";
import { Icon } from "@/components/Icon";
import { useIsMobile } from "@/mobile/useIsMobile";
import {
  MobileProviders, MobileHomeScreen, MobileCasinoScreen, MobileCasinoGameScreen, MobileMinesScreen, MobileDiceScreen, MobileCrashScreen, MobileCoinFlipScreen, MobileRouletteScreen, MobileBlackjackScreen, MobileBaccaratScreen, MobileSlotsScreen, MobileHiLoScreen, MobilePlinkoScreen, MobileKenoScreen, MobileDragonTowerScreen, MobileSportsbookScreen,
  MobileEventScreen, MobileMyBetsScreen, MobileWalletScreen, MobileRewardsScreen,
  MobileAccountScreen, MobileIDCaptureScreen, MobileSignInScreen, MobileRegisterScreen,
} from "@/mobile/MobileRoutes";

function Protected({ children, adminOnly }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Icon name="refresh" size={24} className="animate-spin text-[#00D632]" />
      </div>
    );
  }
  if (user === false) {
    // Preserve the attempted internal destination so Login can return the user here.
    const from = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/login" replace state={{ from }} />;
  }
  if (adminOnly && user.role !== "admin" && user.role !== "owner" && user.role !== "super_admin") return <Navigate to="/" replace />;
  return children;
}

// Only honor an internal, same-origin path (reject "//evil.com", "http(s)://…",
// backslash tricks) so a crafted redirect can't bounce a signed-in user off-site.
function safeInternalPath(p, fallback = "/sportsbook") {
  if (typeof p !== "string" || !p.startsWith("/") || p.startsWith("//") || p.startsWith("/\\")) return fallback;
  return p;
}

// Already-authenticated user hitting /login: send them to their intended destination
// (if one was captured) rather than always to /sportsbook.
function LoginRedirect() {
  const location = useLocation();
  const to = safeInternalPath(location.state?.from);
  return <Navigate to={to} replace />;
}

/* Desktop fallback for /casino/play/:slug (the mobile native-game route): the desktop
   Casino opens games via ?game=…, so a desktop/shared hit redirects there. */
function CasinoPlayDesktopRedirect() {
  const { slug } = useParams();
  return <Navigate to={ORIGINAL_PAGE_ROUTES[slug] || `/casino?game=${encodeURIComponent(slug)}`} replace />;
}

function AppRoutes() {
  const { user } = useAuth();
  // The single viewport boundary (lg = 1024px, matching the existing CSS divide).
  // Mobile mounts the new mobile-native screens; desktop renders the EXACT same
  // elements as before. Only one tree mounts at a time → no duplicate polling.
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const legacyOriginalPath = ORIGINAL_PAGE_ROUTES[searchParams.get("game")];
  const routes = (
    <Routes>
      <Route path="/login" element={user ? <LoginRedirect /> : (isMobile ? <MobileSignInScreen /> : <Login />)} />
      {/* Mobile-only routes — on desktop they preserve the exact pre-change
          fallthrough (there was no /register or /verify-identity route, so "*"
          sent both to "/"). Keeps the desktop contract byte-for-byte. */}
      <Route path="/register" element={isMobile ? (user ? <LoginRedirect /> : <MobileRegisterScreen />) : <Navigate to="/" replace />} />
      <Route path="/verify-identity" element={isMobile ? <Protected><MobileIDCaptureScreen /></Protected> : <Navigate to="/" replace />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Public legal & policy pages — load without auth and from restricted locations (see GeoGate). */}
      <Route path="/legal" element={<LegalHub />} />
      <Route path="/terms" element={<PolicyPage slug="terms" />} />
      <Route path="/sweeps-rules" element={<PolicyPage slug="sweeps-rules" />} />
      <Route path="/privacy" element={<PolicyPage slug="privacy" />} />
      <Route path="/free-entry" element={<PolicyPage slug="free-entry" />} />
      <Route path="/eligible-jurisdictions" element={<PolicyPage slug="eligible-jurisdictions" />} />
      <Route path="/purchases-refunds" element={<PolicyPage slug="purchases-refunds" />} />
      <Route path="/prize-redemptions" element={<PolicyPage slug="prize-redemptions" />} />
      <Route path="/responsible-play" element={<PolicyPage slug="responsible-play" />} />
      <Route path="/game-rules" element={<PolicyPage slug="game-rules" />} />
      <Route path="/complaints" element={<PolicyPage slug="complaints" />} />
      <Route path="/cookies" element={<PolicyPage slug="cookies" />} />
      <Route path="/biometric-notice" element={<PolicyPage slug="biometric-notice" />} />
      <Route path="/payment/success" element={<Protected><PaymentResult /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentResult /></Protected>} />
      <Route
        path="/"
        element={
          user === null ? (
            <div className="flex min-h-screen items-center justify-center">
              <Icon name="refresh" size={24} className="animate-spin text-[#00D632]" />
            </div>
          ) : user ? (
            isMobile ? <MobileHomeScreen /> : <Shell><Home /></Shell>
          ) : (
            <Landing />
          )
        }
      />
      <Route path="/sportsbook" element={<Protected>{isMobile ? <MobileSportsbookScreen /> : <Shell><Sportsbook /></Shell>}</Protected>} />
      {/* UPA/HOF/Dunk (Pro-Am Basketball) sportsbook. Activation is controlled at
          RUNTIME from the admin panel (sb_settings flags), not build env. The
          customer page + operator console respect the runtime flags server-side;
          the operator console is admin-gated. */}
      <Route path="/sportsbook/upa" element={<Protected><SportsbookUPA /></Protected>} />
      <Route path="/admin/sportsbook-upa" element={<Protected adminOnly><OperatorUPA /></Protected>} />
      <Route path="/event/:id/outright" element={<Protected><Shell><OutrightDetail /></Shell></Protected>} />
      <Route path="/event/:id" element={<Protected>{isMobile ? <MobileEventScreen /> : <Shell><EventDetail /></Shell>}</Protected>} />
      <Route path="/wallet" element={<Protected>{isMobile ? <MobileWalletScreen /> : <Shell><Wallet /></Shell>}</Protected>} />
      <Route path="/my-bets" element={<Protected><ConfirmProvider>{isMobile ? <MobileMyBetsScreen /> : <Shell><MyBets /></Shell>}</ConfirmProvider></Protected>} />
      <Route path="/support" element={<Protected><Shell><Support /></Shell></Protected>} />
      <Route path="/streamer" element={<Protected><Shell><Streamer /></Shell></Protected>} />
      <Route path="/casino/mines" element={<Protected>{isMobile ? <MobileMinesScreen /> : <Shell><Mines /></Shell>}</Protected>} />
      <Route path="/casino/dice" element={<Protected>{isMobile ? <MobileDiceScreen /> : <Shell><Dice /></Shell>}</Protected>} />
      <Route path="/casino/crash" element={<Protected>{isMobile ? <MobileCrashScreen /> : <Shell><Crash /></Shell>}</Protected>} />
      <Route path="/casino/coinflip" element={<Protected>{isMobile ? <MobileCoinFlipScreen /> : <Shell><CoinFlip /></Shell>}</Protected>} />
      <Route path="/casino/roulette" element={<Protected>{isMobile ? <MobileRouletteScreen /> : <Shell><Roulette /></Shell>}</Protected>} />
      <Route path="/casino/dragon-tower" element={<Protected>{isMobile ? <MobileDragonTowerScreen /> : <Shell><DragonTower /></Shell>}</Protected>} />
      <Route path="/casino/keno" element={<Protected>{isMobile ? <MobileKenoScreen /> : <Shell><Keno /></Shell>}</Protected>} />
      <Route path="/casino/plinko" element={<Protected>{isMobile ? <MobilePlinkoScreen /> : <Shell><Plinko /></Shell>}</Protected>} />
      <Route path="/casino/hilo" element={<Protected>{isMobile ? <MobileHiLoScreen /> : <Shell><HiLo /></Shell>}</Protected>} />
      <Route path="/casino/slots" element={<Protected>{isMobile ? <MobileSlotsScreen /> : <Shell><Slots /></Shell>}</Protected>} />
      <Route path="/casino/baccarat" element={<Protected>{isMobile ? <MobileBaccaratScreen /> : <Shell><Baccarat /></Shell>}</Protected>} />
      <Route path="/casino/blackjack" element={<Protected>{isMobile ? <MobileBlackjackScreen /> : <Shell><Blackjack /></Shell>}</Protected>} />
      <Route path="/casino/play/:slug" element={<Protected>{isMobile ? <MobileCasinoGameScreen /> : <CasinoPlayDesktopRedirect />}</Protected>} />
      <Route path="/casino" element={<Protected>{legacyOriginalPath ? <Navigate to={legacyOriginalPath} replace /> : isMobile ? <MobileCasinoScreen /> : <Shell><Casino /></Shell>}</Protected>} />
      <Route path="/account" element={<Protected><ConfirmProvider>{isMobile ? <MobileAccountScreen /> : <Shell><Account /></Shell>}</ConfirmProvider></Protected>} />
      <Route path="/rewards" element={<Protected>{isMobile ? <MobileRewardsScreen /> : <Shell><Rewards /></Shell>}</Protected>} />
      <Route path="/store" element={<Protected><Shell><CoinStore /></Shell></Protected>} />
      <Route path="/provider-sandbox/:gameId" element={<Protected><ProviderSandbox /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
      <Route path="/admin/markets" element={<Protected adminOnly><MarketOperations /></Protected>} />
      <Route path="/admin/live-blackjack/:tableId/operator" element={<Protected adminOnly><BlackjackOperator /></Protected>} />
      {/* Admin-protected dealer TV (super-admin only, not a guessable public URL) — auto-mints its own token. */}
      <Route path="/admin/live-blackjack/:tableId/dealer-tv" element={<Protected adminOnly><BlackjackDealerDisplay adminNav /></Protected>} />
      {/* Legacy token-gated link for a separate (non-admin) dealer screen — requires a valid minted token or it shows an access error. */}
      <Route path="/dealer/blackjack/:tableId" element={<BlackjackDealerDisplay />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
  // Mobile subtree gets the mobile prefs + sheet host; desktop is unchanged.
  return isMobile ? <MobileProviders>{routes}</MobileProviders> : routes;
}

function App() {
  return (
    <div className="App">
      <div className="grain" />
      <BrowserRouter>
        <NavigationScroll />
        <AuthProvider>
          <BetSlipProvider>
            <GeoGate>
              <AppRoutes />
              <SportsSearch />
            </GeoGate>
            <CookieConsent />
            <Toaster position="top-center" richColors />
          </BetSlipProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;

